'use client';

import pathlib from 'path';

import { AssetPath, DEFAULT_SCENE_KEY, DEFAULT_SERVER_SCENE_KEY, getStartupScene, loadProjectSettings, PROJECT_PATH_PREFIX } from '@gnsx/genesys.js';
import { reaction } from 'mobx';
import { addDisposer, detach, flow, type Instance, isAlive, t } from 'mobx-state-tree';
import * as THREE from 'three';

import {
  applyOrientationToSize,
  createDeviceConfigFromPreset,
  DEFAULT_PLAY_PREVIEW_CONFIG,
  getPlayDevicePresetById,
  PlayLaunchMode,
  PlayOrientation,
} from '../core/play-preview/playPreviewTypes';
import {
  INSPECTOR_PREVIEW_TONE_MAPPING_CHOICES,
  type InspectorPreviewToneMappingChoice,
} from '../core/preview/resolvePreviewToneMapping';
import { loadScene, resetScene } from '../core/sceneHandler';
import { ThumbnailService } from '../core/thumbnails/thumbnailService';
import { UndoRedoManager } from '../core/undoRedo';


import { AssetsStore } from './assetsStore';
import { AssetTree, normalizeAssetTypeFilterBadges, setAssetTreeEditorStore } from './models/assetTree';
import { InstancedGeometryToolSettings } from './models/instancedGeometryToolSettings';
import { SnapValues } from './models/snapValues';
import { InspectorMode, SelectMode, ViewMode } from './models/types';
import { Project } from './project';
import { SceneStore } from './sceneStore';

import type { IAssetFile, IAssetTypeFilterBadgeState } from './models/assetTree';
import type { DropPayload } from '../core/dragDropTypes';
import type { AssetType } from '@/editor/core/assetTypes';
import type { DragDropHandler } from '@/editor/core/dragAndDrop/dragAndDropHandler';

import { appInfo } from '@/app/sdk';
import {
  DEFAULT_CAMERA_FAR,
  DEFAULT_FOCUS_ZOOM,
  DEFAULT_FOV,
  DEFAULT_NAVMESH_DEBUG_COLOR,
  DEFAULT_OUTLINE_COLOR,
  DEFAULT_OUTLINE_ENABLED,
  DEFAULT_OUTLINE_SHOW_HIDDEN_EDGE,
  DEFAULT_OUTLINE_STRENGTH,
  DEFAULT_OUTLINE_WIDTH,
  DEFAULT_RENDERER_TYPE,
  MAX_CAMERA_FAR,
  MAX_OUTLINE_STRENGTH,
} from '@/const';
import { clearPostProcessExpansionStorage } from '@/editor/property-editor/controls/object-controls/postProcessExpansionStorage';
import { type IPropertyStore, PropertyStore } from '@/editor/property-editor/propertyStore';
import { localStorageHelper } from '@/lib/localStorageHelper';
import { updateProjectSettings } from '@/lib/storage/project-settings';

const DEFAULT_SHOW_PERFORMANCE_MONITOR = false;
const LEGACY_VIEWPORT_SETTINGS_STORAGE_KEY = 'viewportSettings';
const VIEWPORT_SETTINGS_STORAGE_KEY = 'viewportSettings-v2';
const DEFAULT_MULTIPLAYER_LAG_SIMULATION = {
  enabled: false,
  latencyMs: 0,
  jitterMs: 0,
  packetLossPercent: 0,
} as const;

type MultiplayerLagSimulationTarget = 'server' | 'client';

type MultiplayerLagSimulationSettings = {
  enabled: boolean;
  latencyMs: number;
  jitterMs: number;
  packetLossPercent: number;
};

type PersistedMultiplayerLagSimulationConfig = {
  version: 1;
  server: MultiplayerLagSimulationSettings;
  client: MultiplayerLagSimulationSettings;
};

function clampNumber(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) {
    return min;
  }
  return Math.min(max, Math.max(min, Math.round(value)));
}

function readStoredAssetTypeFilterBadges(raw: unknown): IAssetTypeFilterBadgeState[] {
  if (Array.isArray(raw)) {
    return raw
      .filter((type): type is AssetType => typeof type === 'string')
      .map(type => ({ type, enabled: true }));
  }

  if (!raw || typeof raw !== 'object') return [];
  const candidate = raw as Partial<PersistedAssetTypeFilterBadges>;
  if (candidate.version !== 2 || !Array.isArray(candidate.badges)) return [];

  return candidate.badges.filter((badge): badge is IAssetTypeFilterBadgeState => {
    return Boolean(
      badge
      && typeof badge === 'object'
      && typeof badge.type === 'string'
      && typeof badge.enabled === 'boolean',
    );
  });
}

interface ViewportSettingsOverrides {
  showWireframe?: boolean;
  showSkyBox?: boolean;
  showGrid?: boolean;
  showVFX?: boolean;
  showObjectIcons?: boolean;
  fov?: number;
  focusZoom?: number;
  cameraFar?: number;
  outlineEnabled?: boolean;
  outlineShowHiddenEdge?: boolean;
  outlineWidth?: number;
  outlineColor?: string;
  outlineStrength?: number;
  outlineOpacity?: number;
}

const DEFAULT_VIEWPORT_SETTINGS = {
  showWireframe: false,
  showSkyBox: true,
  showGrid: true,
  showVFX: false,
  showObjectIcons: true,
  fov: DEFAULT_FOV,
  focusZoom: DEFAULT_FOCUS_ZOOM,
  cameraFar: DEFAULT_CAMERA_FAR,
  outlineEnabled: DEFAULT_OUTLINE_ENABLED,
  outlineShowHiddenEdge: DEFAULT_OUTLINE_SHOW_HIDDEN_EDGE,
  outlineWidth: DEFAULT_OUTLINE_WIDTH,
  outlineColor: DEFAULT_OUTLINE_COLOR,
  outlineStrength: DEFAULT_OUTLINE_STRENGTH,
} as const;

interface PreviousSelectionSnapshot {
  outlinerNodeIds: string[];
  componentOwnerId: string | null;
  componentNodeIds: string[];
}

interface SceneSettingsPreviousTargetSnapshot {
  mode: 'scene' | 'asset';
  outlinerNodeIds?: string[];
  componentOwnerId?: string | null;
  componentNodeIds?: string[];
  assetPath?: string;
}

interface PersistedAssetTypeFilterBadges {
  version: 2;
  badges: IAssetTypeFilterBadgeState[];
}

/** v1 persisted `window.title` (ignored on load); v2 omits it. */
interface PersistedPlayPreviewConfig {
  version: 1 | 2;
  launchMode: PlayLaunchMode;
  window: {
    width: number;
    height: number;
    devicePixelRatio: number;
    center: boolean;
    resizable: boolean;
  };
  emulation: {
    presetId: string;
    orientation: PlayOrientation;
    width: number;
    height: number;
    devicePixelRatio: number;
    userAgent: string;
    platform: string;
    mobileViewport: boolean;
    touchEmulation: boolean;
    showDeviceFrame: boolean;
    showNotchOverlay: boolean;
    safeAreaInsets: {
      top: number;
      right: number;
      bottom: number;
      left: number;
    };
  };
}

const inspectorPropertyStoreCache = new Map<string, IPropertyStore>();
const inspectorPropertyStoreCacheKeyToStoreId = new Map<string, string>();
let inspectorPropertyStoreCacheOrder: string[] = [];

/** Play Mode Scene Type - determines which scene to load when entering play mode */
export const PlayModeSceneType = {
  GameDefault: 'game_default',
  CurrentScene: 'current_scene',
} as const;
export type PlayModeSceneType = (typeof PlayModeSceneType)[keyof typeof PlayModeSceneType];

/** Constants for thumbnail sizing */
export const THUMBNAIL_SIZES = [
  { cardSize: 80, iconSize: 40, textSize: 11 },
  { cardSize: 100, iconSize: 50, textSize: 12 },
  { cardSize: 120, iconSize: 60, textSize: 13 },
  { cardSize: 150, iconSize: 75, textSize: 14 },
  { cardSize: 180, iconSize: 90, textSize: 14 },
] as const;

/** AssetBrowserPanel Model */
const AssetBrowserPanel = t
  .model('AssetBrowserPanel', {
    isVisible: t.optional(t.boolean, true),
    height: t.number,
    thumbnailStep: t.optional(t.number, 2),
    sidebarCollapsed: t.optional(t.boolean, false),
  })
  .views(self => ({
    get maxThumbnailStep() { return THUMBNAIL_SIZES.length - 1; },
    get thumbnailSize() { return THUMBNAIL_SIZES[self.thumbnailStep].cardSize; },
  }))
  .actions(self => ({
    afterCreate() {
      if (localStorageHelper.isAvailable()) {
        const sidebarCollapsed = localStorageHelper.getBoolean('genesys-assetBrowser-sidebarCollapsed');
        self.sidebarCollapsed = sidebarCollapsed;
      }
    },
    setThumbnailStep(step: number) {
      self.thumbnailStep = step;
    },
    setSidebarCollapsed(collapsed: boolean) {
      self.sidebarCollapsed = collapsed;
      localStorageHelper.setBoolean('genesys-assetBrowser-sidebarCollapsed', collapsed);
    },
  }));

/** InspectorPanel Model */
const InspectorPanel = t
  .model('InspectorPanel', {
    isVisible: t.optional(t.boolean, true),
    propertyStore: t.maybe(PropertyStore),
    showSceneSettingsView: t.optional(t.boolean, false),
    showEngineAssets: t.optional(t.boolean, true),
    showEnginePrefabs: t.optional(t.boolean, false),
    previewToneMapping: t.optional(
      t.enumeration([...INSPECTOR_PREVIEW_TONE_MAPPING_CHOICES]),
      'scene',
    ),
  })
  .views(self => ({
  }))
  .actions(self => ({
    isPropertyStoreCached(store: IPropertyStore): boolean {
      return inspectorPropertyStoreCache.has(store.id);
    },
    getCachedPropertyStore(cacheKey: string): IPropertyStore | undefined {
      const storeId = inspectorPropertyStoreCacheKeyToStoreId.get(cacheKey);
      if (!storeId) return undefined;

      const cachedStore = inspectorPropertyStoreCache.get(storeId);
      if (!cachedStore || !isAlive(cachedStore)) {
        inspectorPropertyStoreCacheKeyToStoreId.delete(cacheKey);
        inspectorPropertyStoreCache.delete(storeId);
        inspectorPropertyStoreCacheOrder = inspectorPropertyStoreCacheOrder.filter(id => id !== storeId);
        return undefined;
      }

      inspectorPropertyStoreCacheOrder = inspectorPropertyStoreCacheOrder.filter(id => id !== storeId);
      inspectorPropertyStoreCacheOrder.push(storeId);
      return cachedStore;
    },
    cachePropertyStore(cacheKey: string, store: IPropertyStore, maxEntries = 4) {
      inspectorPropertyStoreCache.set(store.id, store);
      inspectorPropertyStoreCacheKeyToStoreId.set(cacheKey, store.id);
      inspectorPropertyStoreCacheOrder = inspectorPropertyStoreCacheOrder.filter(id => id !== store.id);
      inspectorPropertyStoreCacheOrder.push(store.id);

      while (inspectorPropertyStoreCacheOrder.length > maxEntries) {
        const evictedStoreId = inspectorPropertyStoreCacheOrder.shift();
        if (!evictedStoreId) continue;

        const evictedStore = inspectorPropertyStoreCache.get(evictedStoreId);
        inspectorPropertyStoreCache.delete(evictedStoreId);
        for (const [key, value] of inspectorPropertyStoreCacheKeyToStoreId.entries()) {
          if (value === evictedStoreId) {
            inspectorPropertyStoreCacheKeyToStoreId.delete(key);
          }
        }

        // Non-current cached stores are already detached roots. Removing them
        // from the cache is enough to let them be collected.
      }
    },
    clearPropertyStoreCache() {
      inspectorPropertyStoreCacheKeyToStoreId.clear();
      inspectorPropertyStoreCacheOrder = [];
      inspectorPropertyStoreCache.clear();
    },
    afterCreate() {
      const showEngineAssets = localStorageHelper.get('genesys-inspector-showEngineAssets');
      self.showEngineAssets = showEngineAssets ? showEngineAssets !== 'false' : self.showEngineAssets;

      const showEnginePrefabs = localStorageHelper.get('genesys-inspector-showEnginePrefabs');
      self.showEnginePrefabs = showEnginePrefabs ? showEnginePrefabs !== 'false' : self.showEnginePrefabs;
    },
    setPreviewToneMapping(value: InspectorPreviewToneMappingChoice) {
      self.previewToneMapping = value;
    },
    setShowSceneSettingsView(show: boolean) {
      self.showSceneSettingsView = show;
    },
    toggleSceneSettingsView() {
      self.showSceneSettingsView = !self.showSceneSettingsView;
    },
    setPropertyStore(store: IPropertyStore | null) {
      if (
        self.propertyStore
        && self.propertyStore !== store
        && isAlive(self.propertyStore)
      ) {
        // Keep the outgoing tree alive for React/MobX references and for the
        // inspector cache. Assigning a new child without detaching would destroy
        // the old MST subtree and produce dead-node reads during render cleanup.
        detach(self.propertyStore);
      }
      self.propertyStore = store ?? undefined;
    },
    setShowEngineAssets(show: boolean) {
      self.showEngineAssets = show;
      localStorageHelper.setBoolean('genesys-inspector-showEngineAssets', show);
    },
    setShowEnginePrefabs(show: boolean) {
      const DEBUG = true;
      if (DEBUG) console.log('🔧 Setting showEnginePrefabs to:', show);
      self.showEnginePrefabs = show;
      localStorageHelper.setBoolean('genesys-inspector-showEnginePrefabs', show);
    }
  }));

/** Outliner panel UI (filter toggles, etc.) */
const OutlinerPanel = t
  .model('OutlinerPanel', {
    showOnlyActorsWithMissingAssets: t.optional(t.boolean, false),
  })
  .actions(self => ({
    toggleShowOnlyActorsWithMissingAssets() {
      self.showOnlyActorsWithMissingAssets = !self.showOnlyActorsWithMissingAssets;
    },
    setShowOnlyActorsWithMissingAssets(value: boolean) {
      self.showOnlyActorsWithMissingAssets = value;
    },
  }));

/** EditorStore */
const EditorStore = t.
  model('EditorStore', {
    scene: SceneStore,
    project: Project,
    assets: AssetsStore,
    inspectorPanel: InspectorPanel,
    outlinerPanel: OutlinerPanel,
    assetBrowser: AssetBrowserPanel,
    transformSpace: t.optional(t.enumeration(['local', 'world']), 'world'),
    viewMode: t.enumeration(Object.values(ViewMode)),
    selectMode: t.enumeration(Object.values(SelectMode)),
    snapValues: SnapValues,
    instancedGeometryToolSettings: InstancedGeometryToolSettings,
    showWireframe: t.optional(t.boolean, false),
    showSkyBox: t.optional(t.boolean, true),
    showGrid: t.optional(t.boolean, true),
    showVFX: t.optional(t.boolean, false),
    showObjectIcons: t.optional(t.boolean, true),
    fov: t.optional(t.number, DEFAULT_FOV),
    focusZoom: t.optional(t.number, DEFAULT_FOCUS_ZOOM),
    cameraFar: t.optional(t.number, DEFAULT_CAMERA_FAR),
    outlineEnabled: t.optional(t.boolean, DEFAULT_OUTLINE_ENABLED),
    outlineShowHiddenEdge: t.optional(t.boolean, DEFAULT_OUTLINE_SHOW_HIDDEN_EDGE),
    outlineWidth: t.optional(t.number, DEFAULT_OUTLINE_WIDTH),
    outlineColor: t.optional(t.string, DEFAULT_OUTLINE_COLOR),
    outlineStrength: t.optional(t.number, DEFAULT_OUTLINE_STRENGTH),
    navMeshDebugColor: t.optional(t.string, DEFAULT_NAVMESH_DEBUG_COLOR),
    showPerformanceMonitor: t.optional(t.boolean, false),
    isBuildingProject: t.optional(t.boolean, false),
    isSaving: t.optional(t.boolean, false),
    isUploadProjectDialogOpen: t.optional(t.boolean, false),
    isModelViewerDialogOpen: t.optional(t.boolean, false),
    isVFXEditorDialogOpen: t.optional(t.boolean, false),
    isAnimationConfigEditorOpen: t.optional(t.boolean, false),
    isSkeletonProfileEditorOpen: t.optional(t.boolean, false),
    /** Observable flag for thumbnail service readiness - used to trigger re-renders when service becomes available */
    isThumbnailServiceReady: t.optional(t.boolean, false),
    /** Default scene for standalone client runtime */
    defaultScene: t.maybeNull(t.string),
    /** Default scene for dedicated server runtime */
    defaultServerScene: t.maybeNull(t.string),
    /** Which scene type to use when entering play mode */
    playModeSceneType: t.optional(t.enumeration(Object.values(PlayModeSceneType)), PlayModeSceneType.GameDefault),
    /** Whether to run play mode with a dedicated multiplayer server. */
    isMultiplayerMode: t.optional(t.boolean, false),
    /**
     * Number of clients to connect when multiplayer mode is active (1–4).
     * 1 = editor window only, 2–4 = editor + N−1 extra play windows.
     */
    multiplayerClientCount: t.optional(t.number, 1),
    multiplayerServerLagEnabled: t.optional(t.boolean, DEFAULT_MULTIPLAYER_LAG_SIMULATION.enabled),
    multiplayerServerLagLatencyMs: t.optional(t.number, DEFAULT_MULTIPLAYER_LAG_SIMULATION.latencyMs),
    multiplayerServerLagJitterMs: t.optional(t.number, DEFAULT_MULTIPLAYER_LAG_SIMULATION.jitterMs),
    multiplayerServerLagPacketLossPercent: t.optional(t.number, DEFAULT_MULTIPLAYER_LAG_SIMULATION.packetLossPercent),
    multiplayerClientLagEnabled: t.optional(t.boolean, DEFAULT_MULTIPLAYER_LAG_SIMULATION.enabled),
    multiplayerClientLagLatencyMs: t.optional(t.number, DEFAULT_MULTIPLAYER_LAG_SIMULATION.latencyMs),
    multiplayerClientLagJitterMs: t.optional(t.number, DEFAULT_MULTIPLAYER_LAG_SIMULATION.jitterMs),
    multiplayerClientLagPacketLossPercent: t.optional(t.number, DEFAULT_MULTIPLAYER_LAG_SIMULATION.packetLossPercent),
    /** Where Play launches: embedded view, new window, or mobile preview window. */
    playLaunchMode: t.optional(t.enumeration(Object.values(PlayLaunchMode)), DEFAULT_PLAY_PREVIEW_CONFIG.launchMode),
    /** Preferred dimensions for "Play in New Window". */
    playWindowWidth: t.optional(t.number, DEFAULT_PLAY_PREVIEW_CONFIG.window.width),
    playWindowHeight: t.optional(t.number, DEFAULT_PLAY_PREVIEW_CONFIG.window.height),
    playWindowDevicePixelRatio: t.optional(t.number, DEFAULT_PLAY_PREVIEW_CONFIG.window.devicePixelRatio),
    playWindowCenter: t.optional(t.boolean, DEFAULT_PLAY_PREVIEW_CONFIG.window.center),
    playWindowResizable: t.optional(t.boolean, DEFAULT_PLAY_PREVIEW_CONFIG.window.resizable),
    /** Device emulation profile */
    playPreviewPresetId: t.optional(t.string, DEFAULT_PLAY_PREVIEW_CONFIG.emulation.presetId),
    playPreviewOrientation: t.optional(t.enumeration(Object.values(PlayOrientation)), DEFAULT_PLAY_PREVIEW_CONFIG.emulation.orientation),
    playPreviewWidth: t.optional(t.number, DEFAULT_PLAY_PREVIEW_CONFIG.emulation.width),
    playPreviewHeight: t.optional(t.number, DEFAULT_PLAY_PREVIEW_CONFIG.emulation.height),
    playPreviewDevicePixelRatio: t.optional(t.number, DEFAULT_PLAY_PREVIEW_CONFIG.emulation.devicePixelRatio),
    playPreviewUserAgent: t.optional(t.string, DEFAULT_PLAY_PREVIEW_CONFIG.emulation.userAgent),
    playPreviewPlatform: t.optional(t.string, DEFAULT_PLAY_PREVIEW_CONFIG.emulation.platform),
    playPreviewMobileViewport: t.optional(t.boolean, DEFAULT_PLAY_PREVIEW_CONFIG.emulation.mobileViewport),
    playPreviewTouchEmulation: t.optional(t.boolean, DEFAULT_PLAY_PREVIEW_CONFIG.emulation.touchEmulation),
    playPreviewShowDeviceFrame: t.optional(t.boolean, DEFAULT_PLAY_PREVIEW_CONFIG.emulation.showDeviceFrame),
    playPreviewShowNotchOverlay: t.optional(t.boolean, DEFAULT_PLAY_PREVIEW_CONFIG.emulation.showNotchOverlay),
    playPreviewSafeAreaTop: t.optional(t.number, DEFAULT_PLAY_PREVIEW_CONFIG.emulation.safeAreaInsets.top),
    playPreviewSafeAreaRight: t.optional(t.number, DEFAULT_PLAY_PREVIEW_CONFIG.emulation.safeAreaInsets.right),
    playPreviewSafeAreaBottom: t.optional(t.number, DEFAULT_PLAY_PREVIEW_CONFIG.emulation.safeAreaInsets.bottom),
    playPreviewSafeAreaLeft: t.optional(t.number, DEFAULT_PLAY_PREVIEW_CONFIG.emulation.safeAreaInsets.left),
    /** Whether the Object Palette is expanded in the viewport */
    isObjectsToolbarVisible: t.optional(t.boolean, true),
    /** Whether Agent Nova (SDK chat panel) is enabled from Tools menu */
    isAgentNovaDevEnabled: t.optional(t.boolean, false),
    /** Whether the agent chat panel is open */
    isAgentChatOpen: t.optional(t.boolean, false),
    /** Whether the DevPanel is open */
    isDevPanelOpen: t.optional(t.boolean, false),
    /** Active tab in the DevPanel */
    devPanelActiveTab: t.optional(t.string, 'debug'),
  })
  .volatile(() => ({
    altPressed: false,
    isCameraFlyControls: false,
    cameraFlySpeed: 1,
    cameraFlySpeedBoost: 1,
    dragDropHandler: null as DragDropHandler | null,
    dragPayload: null as DropPayload | null,
    wireframeMaterial: new THREE.MeshBasicMaterial({ color: 0x00FFFF, wireframe: true, transparent: false, opacity: 0.5 }),
    undoRedoManager: new UndoRedoManager(),
    thumbnailService: null as ThumbnailService | null,
    prevSelectMode: null as SelectMode | null,
    modelViewerAsset: null as IAssetFile | null,
    vfxEditorAssetPath: null as string | null,
    animationConfigEditorPath: null as string | null,
    animationConfigEditorModelPath: null as string | null, // Optional model path to auto-select in the animation editor
    skeletonProfileEditorPath: null as string | null, // Path to skeleton profile being edited
    /** Viewport panel hole in client coordinates (getBoundingClientRect) for gizmo positioning */
    viewportPanelRect: { left: 0, top: 0, width: 0, height: 0 } as {
      left: number;
      top: number;
      width: number;
      height: number;
    },
    /** Active view in the editor: 'editor' or 'wizard' */
    activeView: 'editor' as 'editor' | 'wizard',
    previousSelectionSnapshot: null as PreviousSelectionSnapshot | null,
    sceneSettingsPreviousTargetSnapshot: null as SceneSettingsPreviousTargetSnapshot | null,
  }))
  .views(self => ({
    get transformMode() { return self.selectMode == SelectMode.Select ? undefined : self.selectMode as 'translate' | 'rotate' | 'scale'; },
    get transformModeSpace() { return self.selectMode == SelectMode.Scale ? 'local' : self.transformSpace; },
    get selectedObject() { return self.scene.selectedObject; },
    get selectedObjects() { return self.scene.selectedObjects; },
    get selectedAssetItem(): IAssetFile | undefined {
      if (this.selectedObjects.length > 0 && !this.isPrefabMode) return undefined;
      return self.assets.assetTree.selectedItem;
    },
    get canRestorePreviousSelection() {
      return !!(
        self.previousSelectionSnapshot &&
        self.assets.assetTree.selectedItem &&
        self.scene.selectedObjects.length === 0
      );
    },
    get canRestoreSceneSettingsPreviousTarget() {
      return self.sceneSettingsPreviousTargetSnapshot != null;
    },
    get isPrefabMode() { return self.scene.isPrefabMode; },
    get isPrefabInstanceMode() { return self.scene.selectedObject?.isPrefabInstance ?? false; },
    get inspectorMode() { return this.isPrefabMode ? InspectorMode.PrefabAsset : this.isPrefabInstanceMode ? InspectorMode.PrefabInstance : InspectorMode.Default; },
    get cameraFlySpeedDisplay() { return Math.max(0, self.cameraFlySpeed / Math.max(0.001, self.cameraFlySpeedBoost ?? 1)).toFixed(1) + (self.cameraFlySpeedBoost > 1 ? '++' : ''); },
    /** Returns the scene path to use for play mode based on playModeSceneType */
    get playModeScenePath(): string | null {
      switch (self.playModeSceneType) {
        case PlayModeSceneType.GameDefault:
          return self.defaultScene;
        case PlayModeSceneType.CurrentScene:
          return self.project.currentScene;
        default:
          return self.defaultScene;
      }
    },
    /**
     * Returns the scene path to load on the **dedicated server** when entering multiplayer
     * play mode.  For `GameDefault` this is `defaultServerScene` (not the client scene);
     * for all other types it mirrors `playModeScenePath`.
     */
    get playModeServerScenePath(): string | null {
      switch (self.playModeSceneType) {
        case PlayModeSceneType.GameDefault:
          return self.defaultServerScene;
        case PlayModeSceneType.CurrentScene:
          return self.project.currentScene;
        default:
          return self.defaultServerScene;
      }
    },
    /** Number of extra play windows to open beyond the embedded editor view (0 when not in multiplayer). */
    get extraWindowCount(): number { return self.isMultiplayerMode ? Math.max(0, self.multiplayerClientCount - 1) : 0; },
    get playPreviewSafeAreaInsets() {
      return {
        top: self.playPreviewSafeAreaTop,
        right: self.playPreviewSafeAreaRight,
        bottom: self.playPreviewSafeAreaBottom,
        left: self.playPreviewSafeAreaLeft,
      };
    },
    get playMobilePreviewConfig() {
      return {
        presetId: self.playPreviewPresetId,
        presetName: getPlayDevicePresetById(self.playPreviewPresetId).name,
        width: self.playPreviewWidth,
        height: self.playPreviewHeight,
        orientation: self.playPreviewOrientation,
        devicePixelRatio: self.playPreviewDevicePixelRatio,
        userAgent: self.playPreviewUserAgent,
        platform: self.playPreviewPlatform,
        mobileViewport: self.playPreviewMobileViewport,
        touchEmulation: self.playPreviewTouchEmulation,
        showDeviceFrame: self.playPreviewShowDeviceFrame,
        showNotchOverlay: self.playPreviewShowNotchOverlay,
        safeAreaInsets: this.playPreviewSafeAreaInsets,
      };
    },
  }))
  .actions(self => ({
    captureSceneSettingsPreviousTargetSnapshot() {
      const selectedAsset = (
        self.scene.selectedObjects.length > 0 && !(self.scene.selectedObject?.isPrefabAsset ?? false)
      )
        ? undefined
        : self.assets.assetTree.selectedItem;
      if (selectedAsset?.path) {
        self.sceneSettingsPreviousTargetSnapshot = {
          mode: 'asset',
          assetPath: selectedAsset.path,
        };
        return;
      }

      const outlinerNodeIds = self.scene.outliner.selectedNodeIds
        .filter(id => self.scene.outliner.getNodeById(id));
      if (outlinerNodeIds.length === 0) {
        self.sceneSettingsPreviousTargetSnapshot = null;
        return;
      }

      const primarySelectedObject = self.scene.selectedObjects[0];
      const componentNodeIds = primarySelectedObject
        ? primarySelectedObject.hierarchy.selectedNodeIds.filter(id => primarySelectedObject.hierarchy.getNodeById(id))
        : [];

      self.sceneSettingsPreviousTargetSnapshot = {
        mode: 'scene',
        outlinerNodeIds,
        componentOwnerId: primarySelectedObject?.id ?? null,
        componentNodeIds,
      };
    },
    clearSceneSettingsPreviousTargetSnapshot() {
      self.sceneSettingsPreviousTargetSnapshot = null;
    },
    restoreSceneSettingsPreviousTarget() {
      const snapshot = self.sceneSettingsPreviousTargetSnapshot;
      self.inspectorPanel.setShowSceneSettingsView(false);
      if (!snapshot) return false;

      if (snapshot.mode === 'asset') {
        self.scene.selectObject(null);
        const restoredAsset = snapshot.assetPath
          ? self.assets.assetTree.findFile(snapshot.assetPath)
          : undefined;
        self.assets.assetTree.setSelectedItem(restoredAsset);
        self.sceneSettingsPreviousTargetSnapshot = null;
        return restoredAsset != null;
      }

      const validOutlinerNodeIds = (snapshot.outlinerNodeIds ?? [])
        .filter(id => self.scene.outliner.getNodeById(id));
      if (validOutlinerNodeIds.length === 0) {
        self.sceneSettingsPreviousTargetSnapshot = null;
        return false;
      }

      self.scene.outliner.setSelectedNodeIds(validOutlinerNodeIds);

      const snapshotComponentNodeIds = snapshot.componentNodeIds ?? [];
      if (snapshotComponentNodeIds.length > 0) {
        const restoredObject = snapshot.componentOwnerId
          ? self.scene.selectedObjects.find(obj => obj.id === snapshot.componentOwnerId) ?? self.scene.selectedObjects[0]
          : self.scene.selectedObjects[0];

        if (restoredObject) {
          const validComponentNodeIds = snapshotComponentNodeIds
            .filter(id => restoredObject.hierarchy.getNodeById(id));
          if (validComponentNodeIds.length > 0) {
            restoredObject.hierarchy.setSelectedNodeIds(validComponentNodeIds);
          } else {
            restoredObject.hierarchy.selectNode(restoredObject.id, false);
          }
        }
      }

      self.assets.assetTree.setSelectedItem(undefined);
      self.sceneSettingsPreviousTargetSnapshot = null;
      return true;
    },
    capturePreviousSceneSelectionSnapshot() {
      if (self.scene.selectedObjects.length === 0) {
        return;
      }

      const outlinerNodeIds = self.scene.outliner.selectedNodeIds
        .filter(id => self.scene.outliner.getNodeById(id));

      if (outlinerNodeIds.length === 0) {
        return;
      }

      const primarySelectedObject = self.scene.selectedObjects[0];
      const componentNodeIds = primarySelectedObject
        ? primarySelectedObject.hierarchy.selectedNodeIds.filter(id => primarySelectedObject.hierarchy.getNodeById(id))
        : [];

      self.previousSelectionSnapshot = {
        outlinerNodeIds,
        componentOwnerId: primarySelectedObject?.id ?? null,
        componentNodeIds,
      };
    },
    clearPreviousSelectionSnapshot() {
      self.previousSelectionSnapshot = null;
    },
    restorePreviousSelectionFromAssetFocus() {
      const snapshot = self.previousSelectionSnapshot;
      if (!snapshot) return false;

      const validOutlinerNodeIds = snapshot.outlinerNodeIds
        .filter(id => self.scene.outliner.getNodeById(id));

      if (validOutlinerNodeIds.length === 0) {
        self.previousSelectionSnapshot = null;
        return false;
      }

      self.scene.outliner.setSelectedNodeIds(validOutlinerNodeIds);

      if (snapshot.componentNodeIds.length > 0) {
        const restoredObject = snapshot.componentOwnerId
          ? self.scene.selectedObjects.find(obj => obj.id === snapshot.componentOwnerId) ?? self.scene.selectedObjects[0]
          : self.scene.selectedObjects[0];

        if (restoredObject) {
          const validComponentNodeIds = snapshot.componentNodeIds
            .filter(id => restoredObject.hierarchy.getNodeById(id));
          if (validComponentNodeIds.length > 0) {
            restoredObject.hierarchy.setSelectedNodeIds(validComponentNodeIds);
          } else {
            restoredObject.hierarchy.selectNode(restoredObject.id, false);
          }
        }
      }

      self.assets.assetTree.setSelectedItem(undefined);
      self.previousSelectionSnapshot = null;
      return true;
    },
    // Asset file or folder
    setSelectedAssetItem(asset: IAssetFile | undefined) {
      self.inspectorPanel.setShowSceneSettingsView(false);
      this.clearSceneSettingsPreviousTargetSnapshot();
      self.assets.assetTree.setSelectedItem(asset);

      // While editing a prefab, asset clicks should not change scene selection.
      if (self.isPrefabMode) {
        return;
      }

      if (!asset) {
        this.clearPreviousSelectionSnapshot();
        self.scene.selectObject(null);
        return;
      }

      this.capturePreviousSceneSelectionSnapshot();
      self.scene.selectObject(null);
    },
    setSelectedAssetItemByPath(assetPath: string) {
      self.inspectorPanel.setShowSceneSettingsView(false);
      this.clearSceneSettingsPreviousTargetSnapshot();
      if (!assetPath) {
        this.clearPreviousSelectionSnapshot();
        self.scene.selectObject(null);
        return;
      }

      let selectedAsset: IAssetFile | undefined;
      const assetFile = self.assets.assetTree.findFile(assetPath);
      if (assetFile) {
        self.assets.assetTree.setSelectedItem(assetFile);
        selectedAsset = assetFile;
      }

      // While editing a prefab, asset clicks should not change scene selection.
      if (self.isPrefabMode) {
        return;
      }

      if (!selectedAsset) {
        this.clearPreviousSelectionSnapshot();
        self.scene.selectObject(null);
        return;
      }

      this.capturePreviousSceneSelectionSnapshot();
      self.scene.selectObject(null);
    },
    setTransformSpace(transformSpace: 'world' | 'local') {
      if (self.selectMode === SelectMode.Scale) return; // Scale is always in local space
      self.transformSpace = transformSpace;
    },
    setViewMode(mode: ViewMode) {
      self.viewMode = mode;
      if (mode !== ViewMode.Perspective) {
        self.isCameraFlyControls = false;
      }
    },
    setSelectMode(mode: SelectMode) {
      if (self.isCameraFlyControls) return;
      self.selectMode = mode;
      self.prevSelectMode = mode;
    },
    toggleSelectMode() {
      if (self.isCameraFlyControls) return;
      // if select mode is select, then set it to previous mode if value otherwise translate
      if (self.selectMode === SelectMode.Select) {
        self.selectMode = self.prevSelectMode ?? SelectMode.Translate;
      } else {
        self.selectMode = SelectMode.Select;
      }
    },
    setPlayModeSceneType(type: PlayModeSceneType) {
      self.playModeSceneType = type;
    },
    setIsMultiplayerMode(value: boolean) {
      self.isMultiplayerMode = value;
    },
    /** Set the number of clients to connect when multiplayer mode is active (clamped to 1–4). */
    setMultiplayerClientCount(count: number) {
      self.multiplayerClientCount = Math.min(4, Math.max(1, Math.round(count)));
    },
    setMultiplayerLagEnabled(target: MultiplayerLagSimulationTarget, enabled: boolean) {
      if (target === 'server') {
        self.multiplayerServerLagEnabled = enabled;
      } else {
        self.multiplayerClientLagEnabled = enabled;
      }
    },
    setMultiplayerLagLatencyMs(target: MultiplayerLagSimulationTarget, latencyMs: number) {
      const value = clampNumber(latencyMs, 0, 500);
      if (target === 'server') {
        self.multiplayerServerLagLatencyMs = value;
      } else {
        self.multiplayerClientLagLatencyMs = value;
      }
    },
    setMultiplayerLagJitterMs(target: MultiplayerLagSimulationTarget, jitterMs: number) {
      const value = clampNumber(jitterMs, 0, 100);
      if (target === 'server') {
        self.multiplayerServerLagJitterMs = value;
      } else {
        self.multiplayerClientLagJitterMs = value;
      }
    },
    setMultiplayerLagPacketLossPercent(target: MultiplayerLagSimulationTarget, packetLossPercent: number) {
      const value = clampNumber(packetLossPercent, 0, 100);
      if (target === 'server') {
        self.multiplayerServerLagPacketLossPercent = value;
      } else {
        self.multiplayerClientLagPacketLossPercent = value;
      }
    },
    setPlayLaunchMode(mode: PlayLaunchMode) {
      self.playLaunchMode = mode;
    },
    setPlayWindowSize(width: number, height: number) {
      self.playWindowWidth = Math.max(240, Math.round(width));
      self.playWindowHeight = Math.max(240, Math.round(height));
    },
    setPlayWindowDevicePixelRatio(value: number) {
      self.playWindowDevicePixelRatio = Math.max(0.5, Math.min(4, value));
    },
    setPlayWindowCentered(center: boolean) {
      self.playWindowCenter = center;
    },
    setPlayWindowResizable(resizable: boolean) {
      self.playWindowResizable = resizable;
    },
    setPlayDevicePreset(presetId: string) {
      const emulation = createDeviceConfigFromPreset(presetId, self.playPreviewOrientation);
      self.playPreviewPresetId = emulation.presetId;
      self.playPreviewWidth = emulation.width;
      self.playPreviewHeight = emulation.height;
      self.playPreviewDevicePixelRatio = emulation.devicePixelRatio;
      self.playPreviewUserAgent = emulation.userAgent;
      self.playPreviewPlatform = emulation.platform;
      self.playPreviewMobileViewport = emulation.mobileViewport;
      self.playPreviewTouchEmulation = emulation.touchEmulation;
      self.playPreviewShowDeviceFrame = emulation.showDeviceFrame;
      self.playPreviewShowNotchOverlay = emulation.showNotchOverlay;
      self.playPreviewSafeAreaTop = emulation.safeAreaInsets.top;
      self.playPreviewSafeAreaRight = emulation.safeAreaInsets.right;
      self.playPreviewSafeAreaBottom = emulation.safeAreaInsets.bottom;
      self.playPreviewSafeAreaLeft = emulation.safeAreaInsets.left;
    },
    setPlayOrientation(orientation: PlayOrientation) {
      self.playPreviewOrientation = orientation;
      const size = applyOrientationToSize(self.playPreviewWidth, self.playPreviewHeight, orientation);
      self.playPreviewWidth = Math.round(size.width);
      self.playPreviewHeight = Math.round(size.height);
    },
    setPlayPreviewCustomSize(width: number, height: number) {
      const size = applyOrientationToSize(
        Math.max(120, Math.round(width)),
        Math.max(120, Math.round(height)),
        self.playPreviewOrientation,
      );
      self.playPreviewWidth = size.width;
      self.playPreviewHeight = size.height;
    },
    setPlayDevicePixelRatio(value: number) {
      self.playPreviewDevicePixelRatio = Math.max(0.5, Math.min(4, value));
    },
    setPlaySafeAreaInsets(top: number, right: number, bottom: number, left: number) {
      self.playPreviewSafeAreaTop = Math.max(0, Math.round(top));
      self.playPreviewSafeAreaRight = Math.max(0, Math.round(right));
      self.playPreviewSafeAreaBottom = Math.max(0, Math.round(bottom));
      self.playPreviewSafeAreaLeft = Math.max(0, Math.round(left));
    },
    setPlaySafeAreaInset(side: 'top' | 'right' | 'bottom' | 'left', value: number) {
      const safeValue = Math.max(0, Math.round(value));
      switch (side) {
        case 'top':
          self.playPreviewSafeAreaTop = safeValue;
          break;
        case 'right':
          self.playPreviewSafeAreaRight = safeValue;
          break;
        case 'bottom':
          self.playPreviewSafeAreaBottom = safeValue;
          break;
        case 'left':
          self.playPreviewSafeAreaLeft = safeValue;
          break;
      }
    },
    toggleTouchEmulation(value?: boolean) {
      self.playPreviewTouchEmulation = typeof value === 'boolean' ? value : !self.playPreviewTouchEmulation;
    },
    setPlayMobileViewport(value: boolean) {
      self.playPreviewMobileViewport = value;
    },
    toggleDeviceFrame(value?: boolean) {
      self.playPreviewShowDeviceFrame = typeof value === 'boolean' ? value : !self.playPreviewShowDeviceFrame;
    },
    toggleNotchOverlay(value?: boolean) {
      self.playPreviewShowNotchOverlay = typeof value === 'boolean' ? value : !self.playPreviewShowNotchOverlay;
    },
    setPlayPreviewUserAgent(userAgent: string) {
      self.playPreviewUserAgent = userAgent.trim();
    },
    setPlayPreviewPlatform(platform: string) {
      self.playPreviewPlatform = platform.trim();
    },
    resetPlayPreviewSettings() {
      self.playLaunchMode = DEFAULT_PLAY_PREVIEW_CONFIG.launchMode;
      self.playWindowWidth = DEFAULT_PLAY_PREVIEW_CONFIG.window.width;
      self.playWindowHeight = DEFAULT_PLAY_PREVIEW_CONFIG.window.height;
      self.playWindowDevicePixelRatio = DEFAULT_PLAY_PREVIEW_CONFIG.window.devicePixelRatio;
      self.playWindowCenter = DEFAULT_PLAY_PREVIEW_CONFIG.window.center;
      self.playWindowResizable = DEFAULT_PLAY_PREVIEW_CONFIG.window.resizable;

      const mobilePreview = createDeviceConfigFromPreset(DEFAULT_PLAY_PREVIEW_CONFIG.emulation.presetId);
      self.playPreviewPresetId = mobilePreview.presetId;
      self.playPreviewOrientation = mobilePreview.orientation;
      self.playPreviewWidth = mobilePreview.width;
      self.playPreviewHeight = mobilePreview.height;
      self.playPreviewDevicePixelRatio = mobilePreview.devicePixelRatio;
      self.playPreviewUserAgent = mobilePreview.userAgent;
      self.playPreviewPlatform = mobilePreview.platform;
      self.playPreviewMobileViewport = mobilePreview.mobileViewport;
      self.playPreviewTouchEmulation = mobilePreview.touchEmulation;
      self.playPreviewShowDeviceFrame = mobilePreview.showDeviceFrame;
      self.playPreviewShowNotchOverlay = mobilePreview.showNotchOverlay;
      self.playPreviewSafeAreaTop = mobilePreview.safeAreaInsets.top;
      self.playPreviewSafeAreaRight = mobilePreview.safeAreaInsets.right;
      self.playPreviewSafeAreaBottom = mobilePreview.safeAreaInsets.bottom;
      self.playPreviewSafeAreaLeft = mobilePreview.safeAreaInsets.left;
    },
    setShowWireframe(show: boolean) {
      self.showWireframe = show;
    },
    setShowSkyBox(show: boolean) {
      self.showSkyBox = show;
    },
    setShowGrid(show: boolean) {
      self.showGrid = show;
    },
    setShowVFX(show: boolean) {
      self.showVFX = show;
    },
    setShowObjectIcons(show: boolean) {
      self.showObjectIcons = show;
    },
    setShowPerformanceMonitor(show: boolean) {
      self.showPerformanceMonitor = show;
    },
    setFov(fov: number) {
      self.fov = Math.max(5, Math.min(170, Math.round(fov)));
    },
    setFocusZoom(focusZoom: number) {
      self.focusZoom = Math.max(1, Math.min(100, Math.round(focusZoom)));
    },
    setCameraFar(far: number) {
      self.cameraFar = Math.max(1, Math.min(MAX_CAMERA_FAR, Math.round(far)));
    },
    setOutlineEnabled(enabled: boolean) {
      self.outlineEnabled = enabled;
    },
    setOutlineShowHiddenEdge(show: boolean) {
      self.outlineShowHiddenEdge = show;
    },
    setOutlineWidth(width: number) {
      self.outlineWidth = Math.max(0.5, Math.min(10, width));
    },
    setOutlineColor(color: string) {
      self.outlineColor = color;
    },
    setOutlineStrength(strength: number) {
      self.outlineStrength = Math.max(0, Math.min(MAX_OUTLINE_STRENGTH, strength));
    },
    setNavMeshDebugColor(color: string) {
      self.navMeshDebugColor = color;
    },
    setDragDropHandler(handler: any) {
      self.dragDropHandler = handler;
    },
    setDragPayload(dragPayload: DropPayload | null) {
      self.dragPayload = dragPayload;
    },
    clearDragPayload() {
      self.dragPayload = null;
    },
    setAltPressed(altPressed: boolean) {
      self.altPressed = altPressed;
    },
    setIsCameraFlyControls(enabled: boolean) {
      self.isCameraFlyControls = enabled;
    },
    setCameraFlySpeed(speed: number, boost: number) {
      self.cameraFlySpeed = speed;
      self.cameraFlySpeedBoost = boost;
    },
    setViewportPanelRect(rect: { left: number; top: number; width: number; height: number }) {
      self.viewportPanelRect = rect;
    },
    setActiveView(view: 'editor' | 'wizard') {
      self.activeView = view;
    },
    setObjectsToolbarVisible(visible: boolean) {
      self.isObjectsToolbarVisible = visible;
    },
    toggleObjectsToolbarVisible() {
      self.isObjectsToolbarVisible = !self.isObjectsToolbarVisible;
    },
    setAgentNovaDevEnabled(enabled: boolean) {
      self.isAgentNovaDevEnabled = enabled;
      if (!enabled) {
        self.isAgentChatOpen = false;
      }
    },
    setAgentChatOpen(open: boolean) {
      self.isAgentChatOpen = self.isAgentNovaDevEnabled ? open : false;
    },
    setDevPanelOpen(open: boolean) {
      self.isDevPanelOpen = open;
    },
    setDevPanelTab(tab: string) {
      self.devPanelActiveTab = tab;
    },
    loadStartupScene: flow(function* _loadStartupScene(): Generator<any, void, any> {
      const startTime = performance.now();

      // Load project settings (renderer type already resolved by resolveRendererType)
      let startupScene: string | null = null;
      try {
        const projectProvider = self.project.makeScopedStorageProvider();
        const [projectFile, projectSettings] = yield loadProjectSettings(
          projectProvider,
          self.project.projectFilePathWithProjectPrefix
        );

        console.log('[EditorStore] Load project settings:', projectSettings);

        const rendererType = projectSettings?.rendererType ?? DEFAULT_RENDERER_TYPE;

        // Only update if different (type was pre-resolved in tryOpenProject).
        if (rendererType !== self.scene.rendererType) {
          console.log('[EditorStore] Renderer type changed:', rendererType);
          self.scene.setRendererType(rendererType);
        }

        // Load default scene settings and convert relative paths to full paths
        const projectDir = pathlib.dirname(self.project.projectFilePathWithProjectPrefix);
        const resolveScenePath = (scenePath: string) => {
          // If path already starts with @project/, use it as-is
          if (scenePath.startsWith(PROJECT_PATH_PREFIX + '/')) {
            return scenePath;
          }
          // If path starts with projectDir, use it as-is
          if (scenePath.startsWith(projectDir)) {
            return scenePath;
          }
          // Otherwise, join with projectDir
          return pathlib.join(projectDir, scenePath);
        };

        if (projectSettings?.[DEFAULT_SCENE_KEY]) {
          self.defaultScene = resolveScenePath(projectSettings[DEFAULT_SCENE_KEY]);
        }
        if (projectSettings?.[DEFAULT_SERVER_SCENE_KEY]) {
          self.defaultServerScene = resolveScenePath(projectSettings[DEFAULT_SERVER_SCENE_KEY]);
        }

        // Get startup scene using getStartupScene utility
        startupScene = yield getStartupScene(projectProvider, { forEditor: true, projectFilePath: self.project.projectFilePathWithProjectPrefix });
        // log startup scene
        console.log('[EditorStore] Startup scene:', startupScene);
      } catch (error) {
        console.warn('[EditorStore] Failed to load project settings for renderer type:', error);
      }

      let sceneToLoad: string | null = null;
      if (self.project.currentScene) {
        console.log('[EditorStore] Load startup scene - using the previously active scene.', self.project.currentScene);
        sceneToLoad = self.project.currentScene;
      } else if (startupScene) {
        console.log('[EditorStore] Load startup scene - using project startup scene:', startupScene);
        sceneToLoad = startupScene;
      }
      console.log('[EditorStore] Load project - with scene', { project: self.project.projectFolder, scene: sceneToLoad });

      if (sceneToLoad) {
        yield loadScene(AssetPath.fromString(sceneToLoad));
      }
      else {
        // happens when there is no scene file at all in the project
        console.log('[EditorStore] Startup scene not found, resetting scene');
        yield resetScene();
      }
      console.log(`[EditorStore] Load startup scene took ${((performance.now() - startTime) / 1000).toFixed(3)}s`);
    }),
    setDefaultScene: flow(function* _setDefaultScene(scenePath: string, type: 'game' | 'server'): Generator<any, void, any> {
      const key = type === 'game' ? DEFAULT_SCENE_KEY : DEFAULT_SERVER_SCENE_KEY;
      const storageProvider = self.project.makeScopedStorageProvider();

      const success = yield updateProjectSettings(
        storageProvider,
        { [key]: scenePath },
        { merge: true, projectFile: self.project.projectFilePathWithProjectPrefix }
      );

      if (success) {
        if (type === 'game') {
          self.defaultScene = scenePath;
        } else {
          self.defaultServerScene = scenePath;
        }
        console.log(`[EditorStore] Set ${type} default scene to:`, scenePath);
      }
    }),
    setIsBuildingProject(isBuilding: boolean) {
      self.isBuildingProject = isBuilding;
    },
    setIsSaving(isSaving: boolean) {
      self.isSaving = isSaving;
    },
    setUploadProjectDialogOpen(isOpen: boolean) {
      self.isUploadProjectDialogOpen = isOpen;
    },
    setModelViewerDialogOpen(isOpen: boolean) {
      self.isModelViewerDialogOpen = isOpen;
    },
    openModelViewerDialog(asset: IAssetFile) {
      self.modelViewerAsset = asset;
      self.isModelViewerDialogOpen = true;
    },
    setVFXEditorDialogOpen(isOpen: boolean) {
      self.isVFXEditorDialogOpen = isOpen;
      if (!isOpen) {
        self.vfxEditorAssetPath = null;
      }
    },
    openVFXEditor(assetPath: string) {
      self.vfxEditorAssetPath = assetPath;
      self.isVFXEditorDialogOpen = true;
    },
    setAnimationConfigEditorOpen(isOpen: boolean) {
      self.isAnimationConfigEditorOpen = isOpen;
      if (!isOpen) {
        self.animationConfigEditorPath = null;
        self.animationConfigEditorModelPath = null;
      }
    },
    openAnimationConfigEditor(assetPath: string, modelPath?: string) {
      self.animationConfigEditorPath = assetPath;
      self.animationConfigEditorModelPath = modelPath ?? null;
      self.isAnimationConfigEditorOpen = true;
    },
    setSkeletonProfileEditorOpen(isOpen: boolean) {
      self.isSkeletonProfileEditorOpen = isOpen;
      if (!isOpen) {
        self.skeletonProfileEditorPath = null;
      }
    },
    openSkeletonProfileEditor(assetPath: string) {
      self.skeletonProfileEditorPath = assetPath;
      self.isSkeletonProfileEditorOpen = true;
    },
  })).actions(self => ({
    makePerProjectSaveKey(key: string) {
      return `genesys-${self.project.projectName.toLowerCase()}-${key}`;
    },
    saveSnapValuesToLocalStorage() {
      if (self.project.projectName) {
        localStorageHelper.setJson(this.makePerProjectSaveKey('viewportSnap'), self.snapValues.values);
      }
    },
    loadSnapValuesFromLocalStorage() {
      const savedValues = self.project.projectName
        ? localStorageHelper.getJson<typeof self.snapValues.values>(this.makePerProjectSaveKey('viewportSnap'))
        : null;
      savedValues && self.snapValues.update(savedValues);
    },
    resetViewportSettingsToDefaults() {
      self.showWireframe = DEFAULT_VIEWPORT_SETTINGS.showWireframe;
      self.showSkyBox = DEFAULT_VIEWPORT_SETTINGS.showSkyBox;
      self.showGrid = DEFAULT_VIEWPORT_SETTINGS.showGrid;
      self.showVFX = DEFAULT_VIEWPORT_SETTINGS.showVFX;
      self.showObjectIcons = DEFAULT_VIEWPORT_SETTINGS.showObjectIcons;
      self.fov = DEFAULT_VIEWPORT_SETTINGS.fov;
      self.focusZoom = DEFAULT_VIEWPORT_SETTINGS.focusZoom;
      self.cameraFar = DEFAULT_VIEWPORT_SETTINGS.cameraFar;
      self.outlineEnabled = DEFAULT_VIEWPORT_SETTINGS.outlineEnabled;
      self.outlineShowHiddenEdge = DEFAULT_VIEWPORT_SETTINGS.outlineShowHiddenEdge;
      self.outlineWidth = DEFAULT_VIEWPORT_SETTINGS.outlineWidth;
      self.outlineColor = DEFAULT_VIEWPORT_SETTINGS.outlineColor;
      self.outlineStrength = DEFAULT_VIEWPORT_SETTINGS.outlineStrength;
    },
    saveViewportSettingsToLocalStorage() {
      if (!self.project.projectName) return;
      const settings = {
        showWireframe: self.showWireframe,
        showSkyBox: self.showSkyBox,
        showGrid: self.showGrid,
        showVFX: self.showVFX,
        showObjectIcons: self.showObjectIcons,
        fov: self.fov,
        focusZoom: self.focusZoom,
        cameraFar: self.cameraFar,
        outlineEnabled: self.outlineEnabled,
        outlineShowHiddenEdge: self.outlineShowHiddenEdge,
        outlineWidth: self.outlineWidth,
        outlineColor: self.outlineColor,
        outlineStrength: self.outlineStrength
      };
      localStorageHelper.setJson(this.makePerProjectSaveKey('viewportSettings'), settings);
    },
    loadViewportSettingsFromLocalStorage() {
      interface ViewportSettings {
        showWireframe?: boolean;
        showSkyBox?: boolean;
        showGrid?: boolean;
        showVFX?: boolean;
        showObjectIcons?: boolean;
        fov?: number;
        focusZoom?: number;
        cameraFar?: number;
        outlineEnabled?: boolean;
        outlineShowHiddenEdge?: boolean;
        outlineWidth?: number;
        outlineColor?: string;
        outlineStrength?: number;
        navMeshDebugColor?: string;
      }
      const settings = localStorageHelper.getJson<ViewportSettings & { outlineOpacity?: number }>(this.makePerProjectSaveKey('viewportSettings'));
      if (!settings) return;
      if (typeof settings.showWireframe === 'boolean') self.showWireframe = settings.showWireframe;
      if (typeof settings.showSkyBox === 'boolean') self.showSkyBox = settings.showSkyBox;
      if (typeof settings.showGrid === 'boolean') self.showGrid = settings.showGrid;
      if (typeof settings.showVFX === 'boolean') self.showVFX = settings.showVFX;
      if (typeof settings.showObjectIcons === 'boolean') self.showObjectIcons = settings.showObjectIcons;
      if (typeof settings.fov === 'number') self.fov = Math.max(5, Math.min(170, Math.round(settings.fov)));
      if (typeof settings.focusZoom === 'number') self.focusZoom = Math.max(1, Math.min(100, Math.round(settings.focusZoom)));
      if (typeof settings.cameraFar === 'number') self.cameraFar = Math.max(1, Math.min(MAX_CAMERA_FAR, Math.round(settings.cameraFar)));
      if (typeof settings.outlineEnabled === 'boolean') self.outlineEnabled = settings.outlineEnabled;
      if (typeof settings.outlineShowHiddenEdge === 'boolean') self.outlineShowHiddenEdge = settings.outlineShowHiddenEdge;
      if (typeof settings.outlineWidth === 'number') self.outlineWidth = Math.max(0.5, Math.min(10, settings.outlineWidth));
      if (typeof settings.outlineColor === 'string') self.outlineColor = settings.outlineColor;
      {
        const raw = settings.outlineStrength ?? settings['outlineOpacity'];
        if (typeof raw === 'number') {
          self.outlineStrength = Math.max(0, Math.min(MAX_OUTLINE_STRENGTH, raw));
        }
      }
      if (typeof settings.navMeshDebugColor === 'string') self.navMeshDebugColor = settings.navMeshDebugColor;
    },
    saveTransformSpaceToLocalStorage() {
      if (self.project.projectName) {
        localStorageHelper.set(this.makePerProjectSaveKey('transformSpace'), self.transformSpace);
      }
    },
    loadTransformSpaceFromLocalStorage() {
      const savedTransformSpace = self.project.projectName
        ? localStorageHelper.get(this.makePerProjectSaveKey('transformSpace'))
        : null;
      if (savedTransformSpace && (savedTransformSpace === 'local' || savedTransformSpace === 'world')) {
        self.transformSpace = savedTransformSpace;
      }
    },
    saveViewModeToLocalStorage() {
      if (self.project.projectName) {
        localStorageHelper.set(this.makePerProjectSaveKey('viewMode'), self.viewMode);
      }
    },
    loadViewModeFromLocalStorage() {
      const savedViewMode = self.project.projectName
        ? localStorageHelper.get(this.makePerProjectSaveKey('viewMode'))
        : null;
      if (savedViewMode && Object.values(ViewMode).includes(savedViewMode as ViewMode)) {
        self.viewMode = savedViewMode as ViewMode;
      }
    },
    saveSelectModeToLocalStorage() {
      if (self.project.projectName) {
        localStorageHelper.set(this.makePerProjectSaveKey('selectMode'), self.selectMode);
      }
    },
    loadSelectModeFromLocalStorage() {
      const savedSelectMode = self.project.projectName
        ? localStorageHelper.get(this.makePerProjectSaveKey('selectMode'))
        : null;
      if (savedSelectMode && Object.values(SelectMode).includes(savedSelectMode as SelectMode)) {
        self.selectMode = savedSelectMode as SelectMode;
      }
    },
    saveThumbnailStepToLocalStorage() {
      if (self.project.projectName) {
        localStorageHelper.setNumber(this.makePerProjectSaveKey('thumbnailStep'), self.assetBrowser.thumbnailStep);
      }
    },
    loadThumbnailStepFromLocalStorage() {
      const step = self.project.projectName
        ? localStorageHelper.getNumber(this.makePerProjectSaveKey('thumbnailStep'))
        : null;
      if (step !== null && step >= 0 && step <= self.assetBrowser.maxThumbnailStep) {
        self.assetBrowser.setThumbnailStep(step);
      }
    },
    saveInspectorPreviewToneMappingToLocalStorage() {
      if (self.project.projectName) {
        localStorageHelper.set(
          this.makePerProjectSaveKey('inspectorPreviewToneMapping'),
          self.inspectorPanel.previewToneMapping,
        );
      }
    },
    loadInspectorPreviewToneMappingFromLocalStorage() {
      const saved = self.project.projectName
        ? localStorageHelper.get(this.makePerProjectSaveKey('inspectorPreviewToneMapping'))
        : null;
      if (
        saved &&
        (INSPECTOR_PREVIEW_TONE_MAPPING_CHOICES as readonly string[]).includes(saved)
      ) {
        self.inspectorPanel.setPreviewToneMapping(saved as InspectorPreviewToneMappingChoice);
      }
    },
    savePlayModeSceneTypeToLocalStorage() {
      if (self.project.projectName) {
        localStorageHelper.set(this.makePerProjectSaveKey('playModeSceneType'), self.playModeSceneType);
      }
    },
    loadPlayModeSceneTypeFromLocalStorage() {
      const savedPlayModeSceneType = self.project.projectName
        ? localStorageHelper.get(this.makePerProjectSaveKey('playModeSceneType'))
        : null;
      if (savedPlayModeSceneType && Object.values(PlayModeSceneType).includes(savedPlayModeSceneType as PlayModeSceneType)) {
        self.playModeSceneType = savedPlayModeSceneType as PlayModeSceneType;
      }
    },
    saveIsMultiplayerModeToLocalStorage() {
      if (self.project.projectName) {
        localStorageHelper.setBoolean(this.makePerProjectSaveKey('isMultiplayerMode'), self.isMultiplayerMode);
      }
    },
    loadIsMultiplayerModeFromLocalStorage() {
      const saved = self.project.projectName
        ? localStorageHelper.getBoolean(this.makePerProjectSaveKey('isMultiplayerMode'))
        : null;
      if (saved !== null) {
        self.isMultiplayerMode = saved;
      }
    },
    saveMultiplayerClientCountToLocalStorage() {
      if (self.project.projectName) {
        localStorageHelper.setNumber(this.makePerProjectSaveKey('multiplayerClientCount'), self.multiplayerClientCount);
      }
    },
    loadMultiplayerClientCountFromLocalStorage() {
      const saved = self.project.projectName
        ? localStorageHelper.getNumber(this.makePerProjectSaveKey('multiplayerClientCount'))
        : null;
      if (saved !== null && saved >= 1 && saved <= 4) {
        self.multiplayerClientCount = Math.round(saved);
      }
    },
    saveMultiplayerLagSimulationToLocalStorage() {
      if (!self.project.projectName) return;
      const payload: PersistedMultiplayerLagSimulationConfig = {
        version: 1,
        server: {
          enabled: self.multiplayerServerLagEnabled,
          latencyMs: self.multiplayerServerLagLatencyMs,
          jitterMs: self.multiplayerServerLagJitterMs,
          packetLossPercent: self.multiplayerServerLagPacketLossPercent,
        },
        client: {
          enabled: self.multiplayerClientLagEnabled,
          latencyMs: self.multiplayerClientLagLatencyMs,
          jitterMs: self.multiplayerClientLagJitterMs,
          packetLossPercent: self.multiplayerClientLagPacketLossPercent,
        },
      };
      localStorageHelper.setJson(this.makePerProjectSaveKey('multiplayerLagSimulation'), payload);
    },
    loadMultiplayerLagSimulationFromLocalStorage() {
      const saved = self.project.projectName
        ? localStorageHelper.getJson<PersistedMultiplayerLagSimulationConfig>(this.makePerProjectSaveKey('multiplayerLagSimulation'))
        : null;
      if (!saved || saved.version !== 1) return;

      self.multiplayerServerLagEnabled = saved.server.enabled;
      self.multiplayerServerLagLatencyMs = clampNumber(saved.server.latencyMs, 0, 500);
      self.multiplayerServerLagJitterMs = clampNumber(saved.server.jitterMs, 0, 100);
      self.multiplayerServerLagPacketLossPercent = clampNumber(saved.server.packetLossPercent, 0, 100);
      self.multiplayerClientLagEnabled = saved.client.enabled;
      self.multiplayerClientLagLatencyMs = clampNumber(saved.client.latencyMs, 0, 500);
      self.multiplayerClientLagJitterMs = clampNumber(saved.client.jitterMs, 0, 100);
      self.multiplayerClientLagPacketLossPercent = clampNumber(saved.client.packetLossPercent, 0, 100);
    },
    savePlayPreviewConfigToLocalStorage() {
      if (!self.project.projectName) return;
      const payload: PersistedPlayPreviewConfig = {
        version: 2,
        launchMode: self.playLaunchMode,
        window: {
          width: self.playWindowWidth,
          height: self.playWindowHeight,
          devicePixelRatio: self.playWindowDevicePixelRatio,
          center: self.playWindowCenter,
          resizable: self.playWindowResizable,
        },
        emulation: {
          presetId: self.playPreviewPresetId,
          orientation: self.playPreviewOrientation,
          width: self.playPreviewWidth,
          height: self.playPreviewHeight,
          devicePixelRatio: self.playPreviewDevicePixelRatio,
          userAgent: self.playPreviewUserAgent,
          platform: self.playPreviewPlatform,
          mobileViewport: self.playPreviewMobileViewport,
          touchEmulation: self.playPreviewTouchEmulation,
          showDeviceFrame: self.playPreviewShowDeviceFrame,
          showNotchOverlay: self.playPreviewShowNotchOverlay,
          safeAreaInsets: {
            top: self.playPreviewSafeAreaTop,
            right: self.playPreviewSafeAreaRight,
            bottom: self.playPreviewSafeAreaBottom,
            left: self.playPreviewSafeAreaLeft,
          },
        },
      };
      localStorageHelper.setJson(this.makePerProjectSaveKey('playPreviewConfig'), payload);
    },
    loadPlayPreviewConfigFromLocalStorage() {
      const parsed = self.project.projectName
        ? localStorageHelper.getJson<unknown>(this.makePerProjectSaveKey('playPreviewConfig'))
        : null;
      if (!parsed || typeof parsed !== 'object') return;
      const value = parsed as Partial<PersistedPlayPreviewConfig> & { version?: number };
      if (value.version !== 1 && value.version !== 2) return;

      if (value.launchMode && Object.values(PlayLaunchMode).includes(value.launchMode)) {
        self.playLaunchMode = value.launchMode;
      }

      const windowConfig = value.window;
      if (windowConfig) {
        if (typeof windowConfig.width === 'number') self.playWindowWidth = Math.max(240, Math.round(windowConfig.width));
        if (typeof windowConfig.height === 'number') self.playWindowHeight = Math.max(240, Math.round(windowConfig.height));
        if (typeof windowConfig.devicePixelRatio === 'number') self.playWindowDevicePixelRatio = Math.max(0.5, Math.min(4, windowConfig.devicePixelRatio));
        if (typeof windowConfig.center === 'boolean') self.playWindowCenter = windowConfig.center;
        if (typeof windowConfig.resizable === 'boolean') self.playWindowResizable = windowConfig.resizable;
      }

      const emulationConfig = value.emulation;
      if (emulationConfig) {
        if (typeof emulationConfig.presetId === 'string' && emulationConfig.presetId.length > 0) {
          self.playPreviewPresetId = emulationConfig.presetId;
        }
        if (emulationConfig.orientation && Object.values(PlayOrientation).includes(emulationConfig.orientation)) {
          self.playPreviewOrientation = emulationConfig.orientation;
        }
        if (typeof emulationConfig.width === 'number') self.playPreviewWidth = Math.max(120, Math.round(emulationConfig.width));
        if (typeof emulationConfig.height === 'number') self.playPreviewHeight = Math.max(120, Math.round(emulationConfig.height));
        if (typeof emulationConfig.devicePixelRatio === 'number') self.playPreviewDevicePixelRatio = Math.max(0.5, Math.min(4, emulationConfig.devicePixelRatio));
        if (typeof emulationConfig.userAgent === 'string') self.playPreviewUserAgent = emulationConfig.userAgent;
        if (typeof emulationConfig.platform === 'string') self.playPreviewPlatform = emulationConfig.platform;
        if (typeof emulationConfig.mobileViewport === 'boolean') self.playPreviewMobileViewport = emulationConfig.mobileViewport;
        if (typeof emulationConfig.touchEmulation === 'boolean') self.playPreviewTouchEmulation = emulationConfig.touchEmulation;
        if (typeof emulationConfig.showDeviceFrame === 'boolean') self.playPreviewShowDeviceFrame = emulationConfig.showDeviceFrame;
        if (typeof emulationConfig.showNotchOverlay === 'boolean') self.playPreviewShowNotchOverlay = emulationConfig.showNotchOverlay;
        if (emulationConfig.safeAreaInsets) {
          if (typeof emulationConfig.safeAreaInsets.top === 'number') self.playPreviewSafeAreaTop = Math.max(0, Math.round(emulationConfig.safeAreaInsets.top));
          if (typeof emulationConfig.safeAreaInsets.right === 'number') self.playPreviewSafeAreaRight = Math.max(0, Math.round(emulationConfig.safeAreaInsets.right));
          if (typeof emulationConfig.safeAreaInsets.bottom === 'number') self.playPreviewSafeAreaBottom = Math.max(0, Math.round(emulationConfig.safeAreaInsets.bottom));
          if (typeof emulationConfig.safeAreaInsets.left === 'number') self.playPreviewSafeAreaLeft = Math.max(0, Math.round(emulationConfig.safeAreaInsets.left));
        }
      }
    },
    saveSelectedAssetTypesToLocalStorage() {
      if (!self.project.projectName) return;
      const payload: PersistedAssetTypeFilterBadges = {
        version: 2,
        badges: self.assets.assetTree.shownAssetTypeBadges.map(badge => ({
          type: badge.type,
          enabled: badge.enabled,
        })),
      };
      localStorageHelper.setJson(this.makePerProjectSaveKey('selectedAssetTypes'), payload);
    },
    loadSelectedAssetTypesFromLocalStorage() {
      const parsed = self.project.projectName
        ? localStorageHelper.getJson<unknown>(this.makePerProjectSaveKey('selectedAssetTypes'))
        : null;
      const normalized = normalizeAssetTypeFilterBadges(readStoredAssetTypeFilterBadges(parsed));
      self.assets.assetTree.setAssetTypeFilterBadges(normalized);
    },
    saveShowPerformanceMonitorToLocalStorage() {
      localStorageHelper.setBoolean('genesys-showPerformanceMonitor', self.showPerformanceMonitor);
    },
    loadShowPerformanceMonitorFromLocalStorage() {
      const savedValue = localStorageHelper.get('genesys-showPerformanceMonitor');
      if (savedValue !== null) {
        self.showPerformanceMonitor = savedValue === 'true';
      } else {
        // Default based on dev mode
        self.showPerformanceMonitor = !!(DEFAULT_SHOW_PERFORMANCE_MONITOR && (appInfo?.isDev ?? false));
      }
    },
    saveObjectsToolbarVisibleToLocalStorage() {
      if (self.project.projectName) {
        localStorageHelper.setBoolean(this.makePerProjectSaveKey('objectsToolbarVisible'), self.isObjectsToolbarVisible);
      }
    },
    loadObjectsToolbarVisibleFromLocalStorage() {
      if (!self.project.projectName) {
        self.isObjectsToolbarVisible = true;
        return;
      }
      const key = this.makePerProjectSaveKey('objectsToolbarVisible');
      const saved = localStorageHelper.get(key);
      self.isObjectsToolbarVisible = saved === null ? true : saved === 'true';
    },
    saveIsAgentChatOpenToLocalStorage() {
      localStorageHelper.setBoolean('genesys-isAgentChatOpen', self.isAgentChatOpen);
    },
    loadIsAgentChatOpenFromLocalStorage() {
      if (!self.isAgentNovaDevEnabled) {
        self.isAgentChatOpen = false;
        return;
      }
      const savedIsAgentChatOpen = localStorageHelper.getBoolean('genesys-isAgentChatOpen');
      self.isAgentChatOpen = savedIsAgentChatOpen;
    },
    saveIsAgentNovaDevEnabledToLocalStorage() {
      localStorageHelper.setBoolean('genesys-isAgentNovaDevEnabled', self.isAgentNovaDevEnabled);
    },
    loadIsAgentNovaDevEnabledFromLocalStorage() {
      const savedIsAgentNovaDevEnabled = localStorageHelper.getBoolean('genesys-isAgentNovaDevEnabled', false);
      self.setAgentNovaDevEnabled(savedIsAgentNovaDevEnabled);
    },
    saveDevPanelStateToLocalStorage() {
      localStorageHelper.setBoolean('genesys-isDevPanelOpen', self.isDevPanelOpen);
      localStorageHelper.set('genesys-devPanelActiveTab', self.devPanelActiveTab);
    },
    loadDevPanelStateFromLocalStorage() {
      const savedIsDevPanelOpen = localStorageHelper.getBoolean('genesys-isDevPanelOpen');
      const savedDevPanelTab = localStorageHelper.get('genesys-devPanelActiveTab');
      self.isDevPanelOpen = savedIsDevPanelOpen;
      if (savedDevPanelTab) {
        self.devPanelActiveTab = savedDevPanelTab;
      }
    },
    resetLayout() {
      // EditorLayout keys (react-resizable-panels)
      localStorageHelper.removeIf(key => key.startsWith('react-resizable-panels:genesys-editor-layout'));

      // InspectorPanel key (@window-splitter/react)
      localStorageHelper.remove('window-splitter:genesys-inspector-panel-group');

      clearPostProcessExpansionStorage();

      // AssetBrowser key (per-project)
      localStorageHelper.remove(`window-splitter:${this.makePerProjectSaveKey('asset-browser')}`);
      localStorageHelper.remove(this.makePerProjectSaveKey('objectsToolbarVisible'));

      // Reset asset browser sidebar collapsed state
      self.assetBrowser.setSidebarCollapsed(false);

      // Reset and clear agent chat panel state
      self.isObjectsToolbarVisible = true;
      self.isAgentChatOpen = false;
      localStorageHelper.remove('genesys-isAgentChatOpen');
      localStorageHelper.remove('genesys-agentChat-expandedAsPercentage');

      // Reset and clear DevPanel state
      self.isDevPanelOpen = false;
      self.devPanelActiveTab = 'debug';
      localStorageHelper.remove('genesys-isDevPanelOpen');
      localStorageHelper.remove('genesys-devPanelActiveTab');

      // Force reload to apply default layout
      window.location.reload();
    }
  })).actions(self => ({
    afterCreate() {
      console.log('[EditorStore] afterCreate');

      // Load global settings
      if (localStorageHelper.isAvailable()) {
        self.loadShowPerformanceMonitorFromLocalStorage();
        self.loadIsAgentNovaDevEnabledFromLocalStorage();
      }

      // TODO: <AY> use onSnapshot with pick/omit to save only the necessary settings to localStorage

      // Set up reaction to watch for snapValues changes
      addDisposer(self, reaction(() => self.snapValues.values, () => self.saveSnapValuesToLocalStorage()));

      // Set up reactions to watch for viewport settings changes
      addDisposer(self, reaction(
        () => ({
          showWireframe: self.showWireframe,
          showSkyBox: self.showSkyBox,
          showGrid: self.showGrid,
          showVFX: self.showVFX,
          showObjectIcons: self.showObjectIcons,
          fov: self.fov,
          focusZoom: self.focusZoom,
          cameraFar: self.cameraFar,
          outlineEnabled: self.outlineEnabled,
          outlineShowHiddenEdge: self.outlineShowHiddenEdge,
          outlineWidth: self.outlineWidth,
          outlineColor: self.outlineColor,
          outlineStrength: self.outlineStrength,
          navMeshDebugColor: self.navMeshDebugColor,
        }),
        () => self.saveViewportSettingsToLocalStorage()
      ));

      // Set up reaction to watch for transform space changes
      addDisposer(self, reaction(() => self.transformSpace, () => self.saveTransformSpaceToLocalStorage()));

      // Set up reaction to watch for view mode changes
      addDisposer(self, reaction(() => self.viewMode, () => self.saveViewModeToLocalStorage()));

      // Set up reaction to watch for select mode changes
      addDisposer(self, reaction(() => self.selectMode, () => self.saveSelectModeToLocalStorage()));

      // Set up reaction to watch for thumbnail step changes
      addDisposer(self, reaction(() => self.assetBrowser.thumbnailStep, () => self.saveThumbnailStepToLocalStorage()));

      // Set up reaction to watch for asset browser badge filter changes
      addDisposer(self, reaction(
        () => self.assets.assetTree.shownAssetTypeBadges.map(badge => `${badge.type}:${badge.enabled}`),
        () => self.saveSelectedAssetTypesToLocalStorage()
      ));

      // Set up reaction to watch for inspector preview tone mapping changes
      addDisposer(self, reaction(() => self.inspectorPanel.previewToneMapping, () => self.saveInspectorPreviewToneMappingToLocalStorage()));

      // Set up reaction to watch for play mode scene type changes
      addDisposer(self, reaction(() => self.playModeSceneType, () => self.savePlayModeSceneTypeToLocalStorage()));

      // Set up reaction to watch for multiplayer mode changes
      addDisposer(self, reaction(() => self.isMultiplayerMode, () => self.saveIsMultiplayerModeToLocalStorage()));

      // Set up reaction to watch for multiplayer client count changes
      addDisposer(self, reaction(() => self.multiplayerClientCount, () => self.saveMultiplayerClientCountToLocalStorage()));

      // Set up reaction to persist multiplayer lag simulation settings.
      addDisposer(self, reaction(
        () => ({
          serverEnabled: self.multiplayerServerLagEnabled,
          serverLatencyMs: self.multiplayerServerLagLatencyMs,
          serverJitterMs: self.multiplayerServerLagJitterMs,
          serverPacketLossPercent: self.multiplayerServerLagPacketLossPercent,
          clientEnabled: self.multiplayerClientLagEnabled,
          clientLatencyMs: self.multiplayerClientLagLatencyMs,
          clientJitterMs: self.multiplayerClientLagJitterMs,
          clientPacketLossPercent: self.multiplayerClientLagPacketLossPercent,
        }),
        () => self.saveMultiplayerLagSimulationToLocalStorage()
      ));

      // Set up reaction to persist play preview settings.
      addDisposer(self, reaction(
        () => ({
          launchMode: self.playLaunchMode,
          playWindowWidth: self.playWindowWidth,
          playWindowHeight: self.playWindowHeight,
          playWindowDevicePixelRatio: self.playWindowDevicePixelRatio,
          playWindowCenter: self.playWindowCenter,
          playWindowResizable: self.playWindowResizable,
          playPreviewPresetId: self.playPreviewPresetId,
          playPreviewOrientation: self.playPreviewOrientation,
          playPreviewWidth: self.playPreviewWidth,
          playPreviewHeight: self.playPreviewHeight,
          playPreviewDevicePixelRatio: self.playPreviewDevicePixelRatio,
          playPreviewUserAgent: self.playPreviewUserAgent,
          playPreviewPlatform: self.playPreviewPlatform,
          playPreviewMobileViewport: self.playPreviewMobileViewport,
          playPreviewTouchEmulation: self.playPreviewTouchEmulation,
          playPreviewShowDeviceFrame: self.playPreviewShowDeviceFrame,
          playPreviewShowNotchOverlay: self.playPreviewShowNotchOverlay,
          playPreviewSafeAreaTop: self.playPreviewSafeAreaTop,
          playPreviewSafeAreaRight: self.playPreviewSafeAreaRight,
          playPreviewSafeAreaBottom: self.playPreviewSafeAreaBottom,
          playPreviewSafeAreaLeft: self.playPreviewSafeAreaLeft,
        }),
        () => self.savePlayPreviewConfigToLocalStorage()
      ));

      // Set up reaction to watch for performance monitor changes
      addDisposer(self, reaction(() => self.showPerformanceMonitor, () => self.saveShowPerformanceMonitorToLocalStorage()));

      // Set up reaction to watch for object palette visibility changes
      addDisposer(self, reaction(() => self.isObjectsToolbarVisible, () => self.saveObjectsToolbarVisibleToLocalStorage()));

      // Set up reaction to watch for Agent Nova dev flag changes
      addDisposer(self, reaction(() => self.isAgentNovaDevEnabled, () => self.saveIsAgentNovaDevEnabledToLocalStorage()));

      // Set up reaction to watch for agent chat panel state changes
      addDisposer(self, reaction(() => self.isAgentChatOpen, () => self.saveIsAgentChatOpenToLocalStorage()));

      // Set up reaction to watch for DevPanel state changes
      addDisposer(self, reaction(
        () => ({ isOpen: self.isDevPanelOpen, tab: self.devPanelActiveTab }),
        () => self.saveDevPanelStateToLocalStorage()
      ));

      // Set up reaction to watch for project changes
      addDisposer(self, reaction(
        () => self.project.project,
        (project) => {
          // If no project is loaded, stop here
          if (!project) {
            return;
          }

          // Load persisted settings for the new project
          try {
            self.loadSnapValuesFromLocalStorage();
            self.loadViewportSettingsFromLocalStorage();
            self.loadTransformSpaceFromLocalStorage();
            self.loadViewModeFromLocalStorage();
            self.loadSelectModeFromLocalStorage();
            self.loadThumbnailStepFromLocalStorage();
            self.loadInspectorPreviewToneMappingFromLocalStorage();
            self.loadPlayModeSceneTypeFromLocalStorage();
            self.loadIsMultiplayerModeFromLocalStorage();
            self.loadMultiplayerClientCountFromLocalStorage();
            self.loadMultiplayerLagSimulationFromLocalStorage();
            self.loadPlayPreviewConfigFromLocalStorage();
            self.loadSelectedAssetTypesFromLocalStorage();
            self.loadObjectsToolbarVisibleFromLocalStorage();
            self.loadIsAgentNovaDevEnabledFromLocalStorage();
            self.loadIsAgentChatOpenFromLocalStorage();
            self.loadDevPanelStateFromLocalStorage();
            console.log('💾 Loaded editor settings from localStorage');
          } catch (error) {
            console.warn('[EditorStore] Failed to load persisted settings:', error);
          }
        },
        { fireImmediately: true }
      ));
    },
    beforeDestroy() {
      self.wireframeMaterial.dispose();

      // Clean up thumbnail service
      if (self.thumbnailService) {
        self.thumbnailService.dispose();
        self.thumbnailService = null;
      }
    },
    // === UndoRedo Actions ===
    async undo(): Promise<boolean> {
      return self.undoRedoManager.undo();
    },
    async redo(): Promise<boolean> {
      return self.undoRedoManager.redo();
    },
    canUndo(): boolean {
      return self.undoRedoManager.hasUndo();
    },
    canRedo(): boolean {
      return self.undoRedoManager.hasRedo();
    },

    /** Initialize thumbnail service */
    initializeThumbnailService() {
      if (!self.thumbnailService) {
        self.thumbnailService = new ThumbnailService();
        self.isThumbnailServiceReady = true;
        console.log('[EditorStore] Thumbnail service initialized.');
      }
    },

    /** Cleanup thumbnail service */
    cleanupThumbnailService: flow(function* _cleanupThumbnailService() {
      if (self.thumbnailService) {
        console.log('[EditorStore] Cleaning up thumbnail service');
        self.thumbnailService.prepareShutdown();
        const thumbnailServiceBecameIdle: boolean = yield self.thumbnailService.waitForIdle();
        if (!thumbnailServiceBecameIdle) {
          console.warn('[EditorStore] Thumbnail service did not become idle before cleanup timeout.');
        }
        try {
          yield self.thumbnailService.forceFlushManifest();
        } catch (error) {
          console.warn('Failed to flush thumbnail manifest:', error);
        }
        self.thumbnailService.dispose();
        self.thumbnailService = null;
        self.isThumbnailServiceReady = false;
      }
    }),
  }));

export interface IEditorStore extends Instance<typeof EditorStore> { }

/** Store instance - initial state */
export const editorStore: IEditorStore = EditorStore.create({
  scene: SceneStore.create({}),
  project: Project.create({}),
  assets: AssetsStore.create({
    assetTree: AssetTree.create({}),
  }),
  viewMode: ViewMode.Perspective,
  selectMode: SelectMode.Translate,
  inspectorPanel: {
  },
  outlinerPanel: {
  },
  assetBrowser: {
    height: 220
  },
  snapValues: {},
  instancedGeometryToolSettings: {},
});
setAssetTreeEditorStore(editorStore);

export const editorScene = editorStore.scene;
export const editorAssets = editorStore.assets;
export const editorProject = editorStore.project;
